import React, { useState } from "react";
import { Send, Loader2, ShoppingBag, Briefcase } from "lucide-react";
import { submitLead } from "../../lib/api";
import { toast } from "sonner";

const initial = { name: "", email: "", phone: "", city: "", message: "" };

export const LeadForm = () => {
    const [tab, setTab] = useState("parceiro"); // default Parceiro
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Nome e email são obrigatórios");
            return;
        }
        setLoading(true);
        try {
            await submitLead({ ...form, interest: tab });
            toast.success("Recebemos a tua mensagem. Entraremos em contacto em breve!");
            setForm(initial);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || "Erro ao enviar. Tenta novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contacto" data-testid="lead-form-section" className="py-20 md:py-28 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                        Vamos falar
                    </span>
                    <h2 className="mt-3 font-display font-medium text-4xl md:text-5xl text-brand-ink leading-tight">
                        Receber mais informação
                    </h2>
                    <p className="mt-4 text-lg text-brand-ink-soft">
                        Diz-nos quem és. Respondemos em menos de 24 horas.
                    </p>
                </div>

                <div className="mt-12 rounded-2xl bg-brand-cream ring-1 ring-brand-green/10 p-6 md:p-10 shadow-soft">
                    {/* Toggle */}
                    <div className="bg-white rounded-full p-1.5 flex mx-auto max-w-md shadow-soft">
                        <button
                            type="button"
                            data-testid="tab-parceiro"
                            onClick={() => setTab("parceiro")}
                            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition-all ${
                                tab === "parceiro"
                                    ? "bg-brand-green text-white shadow-pop"
                                    : "text-brand-ink-soft hover:text-brand-ink"
                            }`}
                        >
                            <Briefcase className="h-4 w-4" />
                            Sou Parceiro
                        </button>
                        <button
                            type="button"
                            data-testid="tab-consumidor"
                            onClick={() => setTab("consumidor")}
                            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition-all ${
                                tab === "consumidor"
                                    ? "bg-brand-orange text-white shadow-pop"
                                    : "text-brand-ink-soft hover:text-brand-ink"
                            }`}
                        >
                            <ShoppingBag className="h-4 w-4" />
                            Sou Consumidor
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-4">
                        <Field
                            label="Nome *"
                            value={form.name}
                            onChange={handle("name")}
                            placeholder="O teu nome"
                            testid="lead-input-name"
                            required
                        />
                        <Field
                            label="Email *"
                            type="email"
                            value={form.email}
                            onChange={handle("email")}
                            placeholder="email@exemplo.com"
                            testid="lead-input-email"
                            required
                        />
                        <Field
                            label="Telefone"
                            value={form.phone}
                            onChange={handle("phone")}
                            placeholder="+351 9XX XXX XXX"
                            testid="lead-input-phone"
                        />
                        <Field
                            label={tab === "parceiro" ? "Cidade / Zona" : "Cidade"}
                            value={form.city}
                            onChange={handle("city")}
                            placeholder={
                                tab === "parceiro" ? "Onde queres abrir o ponto?" : "Onde estás?"
                            }
                            testid="lead-input-city"
                        />
                        <div className="md:col-span-2">
                            <label className="text-sm font-semibold text-brand-ink mb-1.5 block">
                                {tab === "parceiro"
                                    ? "Conta-nos sobre o teu projeto"
                                    : "Mensagem"}
                            </label>
                            <textarea
                                rows={4}
                                value={form.message}
                                onChange={handle("message")}
                                placeholder={
                                    tab === "parceiro"
                                        ? "Já tens espaço? Que modelo te interessa?"
                                        : "Diz-nos algo..."
                                }
                                data-testid="lead-input-message"
                                className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-base text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green transition-colors"
                            />
                        </div>

                        <div className="md:col-span-2 mt-2 flex items-center justify-between gap-4 flex-wrap">
                            <p className="text-xs text-brand-ink-soft">
                                Ao enviar concordas com a nossa política de privacidade.
                            </p>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="lead-submit-button"
                                className="inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-green text-white px-7 py-3.5 text-sm font-semibold transition-all shadow-pop disabled:opacity-60"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                                {loading ? "A enviar..." : "Receber informação"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, testid, ...rest }) => (
    <div>
        <label className="text-sm font-semibold text-brand-ink mb-1.5 block">{label}</label>
        <input
            data-testid={testid}
            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-base text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green transition-colors"
            {...rest}
        />
    </div>
);
